'use client';

import React, { useEffect } from 'react';
import type { PlacesType } from 'react-tooltip';
import { useFormHelper } from '@plura/client/utils';
import { isMobile } from 'react-device-detect';
import cn from "classnames";
import { Icon } from "@plura/client/components";
import Tooltip from '../../atoms/Tooltip';
import './radio-group.css';

interface Option {
  value: string;
  label: string;
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
{
  label?: string;
  error?: string;
  className?: string;
  classLabel?: string;
  classInput?: string;
  icon?: string;
  copy?: boolean;
  tooltip?: string;
  place?: PlacesType;
  trailingComponent?: React.ReactNode;
  options: Option[];
  overrideCheckBoxUI?: boolean;
  item: (value: string, checked: boolean, selectedValue?: string | number | readonly string[]) => React.ReactNode;
  disabledKeySelection?: boolean;
  handleOnChange?: () => void;
  minLabel?: string | React.ReactNode;
  maxLabel?: string | React.ReactNode;
  adaptiveUI?: boolean;
};

const RadioGroup = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    className,
    classLabel,
    classInput,
    icon,
    copy,
    tooltip,
    place,
    trailingComponent,
    options,
    overrideCheckBoxUI,
    disabledKeySelection,
    handleOnChange,
    minLabel,
    maxLabel,
    adaptiveUI,
    ...rest
  } = props;
  const { onValueChange, errorMessage, ...formHelperProps } = useFormHelper(rest);

  useEffect(() => {
    const currentValue = formHelperProps.value;

    options.forEach(option => {
      const checkbox = document.getElementById(`${rest.id}.${option.value}`) as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = currentValue === option.value;
      }
    });
  }, [formHelperProps.value, options, rest.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isMobile && !disabledKeySelection) {
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
          return;
        }

        const el = document.activeElement;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable)) {
          return;
        }

        const option = options.find(opt => opt.value === event.key);

        if (option) {
          const checkbox = document.getElementById(`${rest.id}.${option.value}`) as HTMLInputElement;

          if (checkbox) checkbox.click();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [options, onValueChange]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;
    onValueChange(checked ? value : '');
    if (handleOnChange) setTimeout(() => handleOnChange(), 450);
  };

  return (
    <div className={cn('plura-rg-field', className)}>
      {label && (
        <label className={cn(classLabel, 'mb-3.5 block text-sm font-semibold leading-[24px] text-[#33383f] [.dark-mode_&]:text-[#efefef]')}>
          {label}{" "}
          {tooltip && (
            <Tooltip
              className="relative -top-px [&_svg]:fill-[#9a9fa5]"
              content={tooltip}
              place={place ? place : "right"}
            />
          )}
        </label>
      )}
      <div className={cn('plura-rg-wrap', adaptiveUI && 'plura-rg-wrap--adaptive')}>
        <div className="plura-rg-list">
          {options.map((option) => {
            const isChecked = option.value === formHelperProps.value;

            return (
              <div
                key={option.value}
                className={cn(
                  'plura-rg-item',
                  adaptiveUI && 'plura-rg-item--adaptive',
                  overrideCheckBoxUI && 'plura-rg-item--override',
                )}
              >
                <input
                  id={`${rest.id}.${option.value}`}
                  type="checkbox"
                  {...formHelperProps}
                  name={rest.id}
                  value={option.value}
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  className="plura-rg-input"
                />
                <label htmlFor={`${rest.id}.${option.value}`}>
                  {props.item(option.value, isChecked, formHelperProps.value)}
                </label>
              </div>
            );
          })}
        </div>

        {icon && (
          <div className={cn('icon absolute bottom-0 left-0 top-0 flex h-12 w-12 items-center justify-center')}>
            <i className={icon} />{" "}
          </div>
        )}
        {copy && (
          <button type="button" className="absolute bottom-0 right-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e] hover:[&_svg]:fill-[#2a85ff]">
            <Icon name="copy" className="!size-6" />{" "}
          </button>
        )}

        {
          trailingComponent && (
            <div className="absolute right-1 top-1 z-[2]">
              {trailingComponent}
            </div>
          )
        }

        {errorMessage && (
          <p className="mt-1 text-right text-sm text-[#ff6a55]">{errorMessage}</p>
        )}
      </div>

      {
        minLabel && maxLabel && (
          <div className="plura-rg-range-labels">
            <div>
              <span>{minLabel}</span>
            </div>
            <div>
              <span>{maxLabel}</span>
            </div>
          </div>
        )
      }
    </div>
  );
});

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup
